import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { metadataFillLayout } from 'features/knowledgeDocumentViewer/components/metadata/metadataFillLayout'
import { RevisionFamilyList } from 'features/knowledgeDocumentViewer/components/metadata/RevisionFamilyList'
import {
  getScopeChoiceCopy,
  ScopeSummaryCard,
} from 'features/knowledgeDocumentViewer/components/metadata/ScopeSummaryCard'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { ArrowRight, FileStack, FileText, X } from 'lucide-react'
import React, { useCallback } from 'react'

export type MetadataEditSurface = 'page' | 'document'

const scopeChoiceArrow = <ArrowRight size={16} aria-hidden />

export type ChooseEditScopePanelProps = {
  familySize: number
  currentFilename: string | undefined
  members: readonly RevisionFamilyMemberView[]
  familyScopeDisabled?: boolean
  metadataSurface?: MetadataEditSurface
  onCancel: () => void
  onChooseScope: (scope: MetadataEditScope) => void
  onSelectVersion?: (member: RevisionFamilyMemberView) => void
  onOpenInNewTab?: (member: RevisionFamilyMemberView) => void
}

export function ChooseEditScopePanel({
  familySize,
  currentFilename,
  members,
  familyScopeDisabled = false,
  metadataSurface = 'page',
  onCancel,
  onChooseScope,
  onSelectVersion,
  onOpenInNewTab,
}: ChooseEditScopePanelProps): React.ReactElement {
  const { t } = useLingui()
  const handleChooseSingle = useCallback(() => {
    onChooseScope('single')
  }, [onChooseScope])

  const handleChooseFamily = useCallback(() => {
    if (familyScopeDisabled) return
    onChooseScope('family')
  }, [familyScopeDisabled, onChooseScope])

  const singleCopy = getScopeChoiceCopy({
    scope: 'single',
    familySize,
    metadataSurface,
    currentFilename,
  })
  const familyCopy = getScopeChoiceCopy({
    scope: 'family',
    familySize,
    metadataSurface,
    familyScopeDisabled,
  })

  return (
    <Box display='flex' direction='column' gap='lg' {...metadataFillLayout}>
      <Box
        display='flex'
        align='center'
        justify='between'
        gap='md'
        shrink={false}>
        <Text as='h3' variant='body' weight='semibold'>
          {metadataSurface === 'document'
            ? t`Choose document edit scope`
            : t`Choose page edit scope`}
        </Text>
        <Button
          type='button'
          variant='ghost'
          size='default'
          aria-label={t`Cancel choose edit scope`}
          onClick={onCancel}>
          <X />
          {t`Cancel`}
        </Button>
      </Box>

      <Box shrink={false}>
        <Text variant='body' color='muted'>
          {t`This document belongs to a family of ${familySize} revisions.`}
        </Text>
      </Box>

      <Box display='flex' direction='column' gap='md' shrink={false}>
        <ScopeSummaryCard
          icon={<FileText size={20} />}
          title={singleCopy.title}
          description={singleCopy.description}
          onSelect={handleChooseSingle}
          action={scopeChoiceArrow}
        />
        <ScopeSummaryCard
          icon={<FileStack size={20} />}
          title={familyCopy.title}
          description={familyCopy.description}
          disabled={familyScopeDisabled}
          onSelect={handleChooseFamily}
          action={scopeChoiceArrow}
        />
      </Box>

      <RevisionFamilyList
        members={members}
        onSelectVersion={onSelectVersion}
        onOpenInNewTab={onOpenInNewTab}
      />
    </Box>
  )
}
